import { DigestData, ReportPrintParams } from '@vision/ui/interfaces';
import qs from 'qs';

const ALLOWED_REPORT_PRINT_PARAMS: Array<keyof ReportPrintParams> = [
  'accountId',
  'nodeId',
  'reportId',
  'userId',
  'email',
  'token',
  'print',
  'export',
  'digest',
  'redirect',
  'job_results_key',
  'from_date',
  'to_date',
  'channel_ids',
  'flow_ids',
  'unit_ids',
  'tag_ids',
  'customer_schema_ids',
  'customer_schema_tag_ids',
  'date_range',
  'ticketFilters',
];

export function toReportPrintSearchParams(params: Partial<ReportPrintParams>): URLSearchParams {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    searchParams.set(key, String(value));
  });

  return searchParams;
}

export function isValidBase64(str: string): boolean {
  return /^[A-Za-z0-9+/]*={0,2}$/.test(str);
}

export function decodeBase64Json<T = any>(base64String: string): T | null {
  try {
    if (!isValidBase64(base64String)) {
      return null;
    }
    const decodedHash = atob(base64String);
    return JSON.parse(decodedHash) as T;
  } catch (error) {
    console.error('Failed to decode base64 JSON:', error);
    return null;
  }
}

export function getDigestDataFromUrl(): DigestData | null {
  const params = new URLSearchParams(window.location.search);
  const hashQueryParam = params.get('hash');
  if (hashQueryParam) {
    const digestData = decodeBase64Json<DigestData>(hashQueryParam);
    if (digestData && digestData.job_results && Array.isArray(digestData.job_results)) {
      return digestData;
    }
  }

  return null;
}

export function parseReportPrintParams(): ReportPrintParams {
  const params = new URLSearchParams(window.location.search);
  let parsed = qs.parse(params.toString()) as unknown as ReportPrintParams;

  const digestParam = params.get('digest');
  if (digestParam) {
    parsed = { ...parsed, digest: digestParam === 'true' };
  }

  if (parsed.digest) {
    const hashQueryParam = params.get('hash');
    if (hashQueryParam) {
      try {
        const hashData = decodeBase64Json<DigestData>(hashQueryParam);
        if (hashData && typeof hashData === 'object') {
          const hashParsed = {
            ...parsed,
            ...(hashData.accountId && { accountId: hashData.accountId }),
            ...(hashData.dashboardId && { reportId: hashData.dashboardId }),
            ...(hashData.reportId && { reportId: hashData.reportId }),
            ...(hashData.userId && { userId: hashData.userId }),
            ...(hashData.email && { email: hashData.email }),
            ...(hashData.token && { token: hashData.token }),
          };
          parsed = hashParsed;
        }
      } catch (error) {
        console.error(error);
      }
    }
  }

  const result = Object.entries(parsed).reduce<ReportPrintParams>((acc, [key, value]) => {
    if (!ALLOWED_REPORT_PRINT_PARAMS.includes(key as keyof ReportPrintParams)) {
      return acc;
    }

    return {
      ...acc,
      [key]: value,
    };
  }, {} as ReportPrintParams);

  const userIdParam = params.get('user_id');
  if (userIdParam && !result.userId) {
    result.userId = userIdParam;
  }

  const nodeIdParam = params.get('node_id');
  if (nodeIdParam && !result.nodeId) {
    result.nodeId = nodeIdParam;
  }

  const redirectToParam = params.get('redirect_to');
  if (redirectToParam && !result.redirect) {
    const decodedRedirect = decodeURIComponent(redirectToParam);
    result.redirect = decodedRedirect;
  }

  const jobResultsKeyParam = params.get('job_results_key');
  if (jobResultsKeyParam && !result.job_results_key) {
    result.job_results_key = jobResultsKeyParam;
  }

  return result;
}
